import Form, {
  ButtonItem,
  ButtonOptions,
  EmailRule,
  Item,
  Label,
  RequiredRule,
} from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import notify from "../../lib/notify";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { lockOutDisableLogin } from "../../api/auth";
import { useAuth } from "../../contexts/auth";
import "./LockOutDisableLoginForm.scss";

type LockOutDisableLoginFormProps = {
  email?: string;
  companyId?: number;
  rememberMe?: boolean;
  onTwoFactorRequired?: (rememberMe: boolean) => void;
};

export const LockOutDisableLoginForm = ({
  email = "",
  companyId = 0,
  rememberMe = false,
  onTwoFactorRequired,
}: LockOutDisableLoginFormProps) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const formData = useRef({
    email,
    password: "",
    pesel: "",
    companyId,
    rememberMe,
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setLoading(true);

      const {
        email: formEmail,
        password,
        pesel,
        companyId: formCompanyId,
        rememberMe: formRememberMe,
      } = formData.current;

      try {
        const disableResult = await lockOutDisableLogin(
          formEmail,
          pesel,
          formCompanyId,
        );

        if (!disableResult.isOk) {
          notify(
            disableResult.error || t("common.unknownErrorOccurred"),
            "error",
            5000,
          );
          setLoading(false);
          return;
        }

        if (disableResult.data?.peselLoginBlock) {
          notify(t("accountBlocked"), "error", 5000);
          setLoading(false);
          return;
        }

        const result = await signIn(
          formEmail,
          password,
          formCompanyId,
          formRememberMe,
        );

        if (result.isOk) {
          if (result.requiresTwoFactorAuthentication && onTwoFactorRequired) {
            onTwoFactorRequired(formRememberMe);
          } else {
            navigate("/");
          }
        } else {
          notify(
            result.message || t("common.unknownErrorOccurred"),
            "error",
            5000,
          );
          setLoading(false);
        }
      } catch (error) {
        notify(t("common.unknownErrorOccurred"), "error", 5000);
        setLoading(false);
      }
    },
    [navigate, signIn, t, onTwoFactorRequired],
  );

  return (
    <form className="login-form" onSubmit={onSubmit}>
      <Form
        formData={formData.current}
        disabled={loading}
        showColonAfterLabel
        showRequiredMark
      >
        <Item
          render={() => (
            <div className="account-blocked-info">
              <p>{t("accountBlockedLoginInfo")}</p>
            </div>
          )}
        />
        <Item
          dataField="email"
          editorType="dxTextBox"
          editorOptions={{
            stylingMode: "filled",
            placeholder: t("emailAddress"),
            mode: "email",
          }}
        >
          <RequiredRule message="Email is required" />
          <EmailRule message="Email is invalid" />
          <Label visible={false} />
          <Label text={t("Email")} />
        </Item>
        <Item
          dataField="password"
          editorType="dxTextBox"
          editorOptions={{
            stylingMode: "filled",
            placeholder: t("password"),
            mode: "password",
          }}
        >
          <RequiredRule message="Password is required" />
          <Label visible={false} />
        </Item>
        <Item
          dataField="pesel"
          editorType="dxTextBox"
          editorOptions={{
            stylingMode: "filled",
            placeholder: "PESEL",
          }}
        >
          <RequiredRule message="PESEL is required" />
          <Label visible={false} />
          <Label text="PESEL" />
        </Item>
        <Item
          dataField="rememberMe"
          editorType="dxCheckBox"
          editorOptions={{
            elementAttr: { class: "form-text" },
            text: t("rememberMe"),
          }}
        >
          <Label visible={false} />
        </Item>
        <ButtonItem>
          <ButtonOptions width="100%" type="default" useSubmitBehavior>
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width="24px" height="24px" visible />
              ) : (
                t("signIn")
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );
};
